import * as i0 from '@angular/core';
import { Injectable, Inject } from '@angular/core';
import { Subject, merge, fromEvent, filter, map, share, defer, startWith, distinctUntilChanged } from 'rxjs';
import { DOCUMENT } from '@angular/common';

/**
 * The filter attribute type: datasourceType, datasource, dataset, field
 */
var FilterAttributeType;
(function (FilterAttributeType) {
    /**
     * Filter by datasource type.
     */
    FilterAttributeType["DatasourceType"] = "datasourceType";
    /**
     * Filter by datasource.
     */
    FilterAttributeType["Datasource"] = "datasource";
    /**
     * Filter by dataset.
     */
    FilterAttributeType["Dataset"] = "dataset";
    /**
     * Filter by data type.
     */
    FilterAttributeType["DataType"] = "dataType";
    /**
     * Filter by field.
     */
    FilterAttributeType["ListField"] = "list";
    /**
     * Filter by string.
     */
    FilterAttributeType["StringField"] = "string";
    /**
     * Filter by number.
     */
    FilterAttributeType["NumberField"] = "number";
    /**
     * Filter by boolean.
     */
    FilterAttributeType["BooleanField"] = "boolean";
    /**
     * Filter by date.
     */
    FilterAttributeType["DateField"] = "date";
    /**
     * Filter by score.
     */
    FilterAttributeType["ScoreField"] = "score";
    /**
     * Govern specific field. Filter by a single color name.
     */
    FilterAttributeType["ColorField"] = "color";
    /**
     * Govern specific field. Filter by multiple text inputs (via p-chips component).
     */
    FilterAttributeType["MultiInputField"] = "multi-input";
})(FilterAttributeType || (FilterAttributeType = {}));

/**
 * The filter operator: Is, IsNot, IsPopuplated, IsNotPopulated
 */
var FilterOperator;
(function (FilterOperator) {
    FilterOperator["Is"] = "Is";
    FilterOperator["IsNot"] = "IsNot";
    FilterOperator["IsPopulated"] = "IsPopulated";
    FilterOperator["IsNotPopulated"] = "IsNotPopulated";
    FilterOperator["Contains"] = "Contains";
    FilterOperator["NotContains"] = "NotContains";
    FilterOperator["StartsWith"] = "StartsWith";
    FilterOperator["EndsWith"] = "EndsWith";
    FilterOperator["IsLessThan"] = "IsLessThan";
    FilterOperator["IsGreaterThan"] = "IsGreaterThan";
    FilterOperator["IsGreaterThanOrEqualTo"] = "IsGreaterThanOrEqualTo";
    FilterOperator["IsLessThanOrEqualTo"] = "IsLessThanOrEqualTo";
    FilterOperator["IsBetween"] = "IsBetween";
    FilterOperator["IsOnOrAfter"] = "IsOnOrAfter";
    FilterOperator["IsOnOrBefore"] = "IsOnOrBefore";
    FilterOperator["IsBefore"] = "IsBefore";
    FilterOperator["IsAfter"] = "IsAfter";
    FilterOperator["IsTrue"] = "IsTrue";
    FilterOperator["IsFalse"] = "IsFalse";
    FilterOperator["IsInBand"] = "IsInBand";
})(FilterOperator || (FilterOperator = {}));

/**
 * Provides access to the global window object in a way that is compatible with Angular's dependency
 * injection framework.  This is especially useful for unit testing.
 */
class WindowService {
    /**
     * @internal
     */
    constructor() {
        this.window = window;
    }
    /**
     * Returns the global `window` object.
     */
    get nativeWindow() {
        return this.window;
    }
    /**
     * Injects an arbitrary object into the window for global access.  This can be useful to expose Angular services for the
     * purposes of debugging, e2e tests, etc.
     * @param obj The object to expose on the window.
     * @param namespace
     * @param className
     */
    inject(obj, namespace = 'prismng', className = obj.constructor.name) {
        if (!this.window[namespace]) {
            this.window[namespace] = {};
        }
        this.window[namespace][className] = obj;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: WindowService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: WindowService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: WindowService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class LocalStorageService {
    get localStorage() {
        return this.windowService.nativeWindow.localStorage;
    }
    constructor(windowService) {
        this.windowService = windowService;
        this.storageSubject = new Subject();
        // connect subject to widnow storage events. note that this will only fire if the event is triggered by a different window
        // hence the need for the subject to be called manually when setting an item
        this.storageValues = merge(this.storageSubject, fromEvent(this.windowService.nativeWindow, 'storage').pipe(filter((event) => event.storageArea === this.localStorage), map((event) => ({ key: event.key, value: event.newValue })))).pipe(share());
    }
    /**
     * get an item from local storage
     */
    getItem(key) {
        return this.localStorage.getItem(key);
    }
    /**
     * Listens to a specific item from local storage. Emits the value on distinct changes. Will be
     * triggered when change are made via this service or in other windows. Direct writes to
     * localstorage in this window will not trigger this observable.
     */
    listenToItem(key) {
        return defer(() => {
            return this.storageValues.pipe(filter((data) => data.key === key), map((data) => data.value), startWith(this.getItem(key)), distinctUntilChanged());
        });
    }
    /**
     * remove an item from localstorage
     */
    removeItem(key) {
        this.localStorage.removeItem(key);
        this.storageSubject.next({ key, value: null });
    }
    /**
     * set a key/value pair in localstorage
     */
    setItem(key, value) {
        this.localStorage.setItem(key, value);
        this.storageSubject.next({ key, value });
    }
    ngOnDestroy() {
        this.storageSubject.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: LocalStorageService, deps: [{ token: WindowService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: LocalStorageService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: LocalStorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: WindowService }] });

/**
 * No operation function.
 */
const noop = () => { };

/**
 * The type of card this product corresponds to:  ENABLED, DISABLED, COMING_SOON.
 */
var ProductCardType;
(function (ProductCardType) {
    /**
     * The card should be enabled and support navigation to the representative product.
     */
    ProductCardType["Enabled"] = "ENABLED";
    /**
     * Typically used for products to which a user does not have access.  The card will be disabled and
     * clicking the card does not perform any navigation.  Allows displaying a url link for additional info.
     */
    ProductCardType["Disabled"] = "DISABLED";
    /**
     * Reserved for products that are coming soon.  Clicking the card does not perform any navigation.
     * Allows displaying a url link for additional info.
     */
    ProductCardType["ComingSoon"] = "COMING_SOON";
})(ProductCardType || (ProductCardType = {}));

/**
 * Provides theme related services.
 */
class ThemeService {
    /**
     * @internal
     */
    constructor(window, document) {
        this.window = window;
        this.document = document;
        /**
         * @internal
         * This holds an array of supported themes.
         */
        this.acceptedThemes = ['light', 'dark'];
    }
    /**
     * Checks the url for a theme parameter and sets the theme if it is supported.
     */
    init() {
        let matches = this.window.nativeWindow.location.search.match(/theme=([^&]*)/);
        if (matches && this.acceptedThemes.includes(matches[1])) {
            this.set(matches[1]);
        }
        else {
            this.document.getElementsByTagName('body')[0].classList.remove('dark-mode', 'light-mode');
        }
    }
    /**
     * Get the currently applied theme.
     * @returns the current theme
     */
    get() {
        const body = this.document.getElementsByTagName('body')[0].classList;
        return body.contains('dark-mode') ? 'dark' : 'light';
    }
    /**
     * Sets the theme.  Theme must be one of 'light' or 'dark'.
     */
    set(theme) {
        const body = this.document.getElementsByTagName('body')[0].classList;
        body.contains('light-mode') ? body.remove('light-mode') : body.remove('dark-mode');
        body.add(theme + '-mode');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ThemeService, deps: [{ token: WindowService }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ThemeService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ThemeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: WindowService }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });

/**
 * Generated bundle index. Do not edit.
 */

export { FilterAttributeType, FilterOperator, LocalStorageService, ProductCardType, ThemeService, WindowService, noop };
